import { DateTime, Duration } from 'luxon';

import {
    Patient,
    JobStatus,
    Vaccination,
    Job,
    v2,
    DispositionType,
    dateStringOrUndefined,
    JobType,
    toLuxon,
    Route,
    OnDemand,
} from '../../..';
import { personJobAdapter } from './personJob.adapter';
import { vaccinationAdapter } from './vaccination.adapter';
import { admissionAdapter } from './admission.adapter';
import { contactRecordAdapter } from './contactRecord.adapter';
import { patientAdapter } from './patient.adapter';
import { personAdapter } from './person.adapter';
import { noteAdapter } from './note.adapter';

type ReturnType = Partial<Job> &
    Partial<Patient> &
    Partial<Vaccination> &
    Partial<Route> &
    Partial<OnDemand>;

export const jobAdapter = (input: v2.Job): ReturnType => {
    const {
        id,
        plannedDate,
        plannedDuration,
        plannedTime,
        availableFrom,
        availableTo,
        carRequired,
        version,
        visit,
        administrative,
        patient,
        personJobs,
        updatedAt,
        updatedById,
        createdAt,
        createdById,
        contactReferences,
        unitId,
        latitude,
        longitude,
        jobTypeId,
        notes,
        originalJobInSeriesId,
        personJobAllowedPersonIds,
        personJobNotifiedPersonIds,
        expiryDateTime,
    } = input;

    if (!id) {
        throw new Error('No `id` present in data supplied to adapter');
    }

    const { person, admissions } = patient ?? {};
    const { careComplexity, earliestDateTime, service } = visit ?? {};
    const { activityType } = administrative ?? {};

    const carRequiredSet = typeof carRequired === 'boolean';
    const jobType =
        jobTypeId === 'visit' || jobTypeId === 'administrative' ? JobType.UCR : jobTypeId;

    // Create a basic job object that will then be added to depending on what data is present in
    // the input
    const result: ReturnType = {
        id,
        careComplexity,
        activityType,
        latitude,
        longitude,
        version,
        jobType,
        createdBy: createdById ?? undefined,
        organisation: unitId,
        staffRequired: personJobs?.length,
        carRequired: carRequiredSet ? carRequired : undefined,
        initialVisit: originalJobInSeriesId ?? undefined,
        lastUpdatedBy: updatedById ?? undefined,
        nominatedHcps: personJobAllowedPersonIds ?? undefined,
        hcpsNotified: personJobNotifiedPersonIds ?? undefined,
        hcpNotifiedCount: personJobNotifiedPersonIds?.length ?? undefined,
        createDateTime: dateStringOrUndefined(createdAt) ?? new Date().toISOString(),
        availableFrom: dateStringOrUndefined(availableFrom),
        availableTo: dateStringOrUndefined(availableTo),
        earliestDateOfVisit: dateStringOrUndefined(earliestDateTime),
        startDateTime: dateStringOrUndefined(plannedTime),
        dateOfVisit: dateStringOrUndefined(plannedDate),
        lastUpdatedDateTime: dateStringOrUndefined(updatedAt),
        expiryDateTime: dateStringOrUndefined(expiryDateTime),
    };

    if (plannedDuration) {
        result.duration = Duration.fromObject({ minutes: plannedDuration }).toFormat('hh:mm');
    }

    if (plannedTime && plannedDuration) {
        result.endDateTime = DateTime.fromJSDate(plannedTime)
            .plus({
                minutes: plannedDuration,
            })
            .toUTC()
            .toISO();
    }

    if (activityType) {
        result.disposition = DispositionType.ADMIN;
    } else if (service) {
        result.disposition = service;
    }

    if (personJobs?.length) {
        const {
            hcpId,
            hcpName,
            hcpType,
            hcpGrade,
            madeCurrentDateTime,
            arrivedDateTime,
            finishedDateTime,
            hcpAbortedDateTime,
            abortedBy,
            jobStatus,
            jobStatusBeforeAbort,
            controllerAbortedReason,
            controllerAbortedNotes,
            controllerReverseReason,
            controllerReverseNotes,
            hcpAbortedReason,
            hcpAbortedNotes,
            hcpReverseReason,
            hcpReverseNotes,
            postVisitNotes,
            buddyId,
            buddyName,
            buddyGrade,
            buddyMadeCurrentDateTime,
            buddyArrivedDateTime,
            buddyFinishedDateTime,
            buddyHcpAbortedDateTime,
            buddyAbortedBy,
            buddyJobStatus,
            buddyJobStatusBeforeAbort,
            buddyControllerAbortedReason,
            buddyControllerAbortedNotes,
            buddyControllerReverseReason,
            buddyControllerReverseNotes,
            buddyHcpAbortedReason,
            buddyHcpAbortedNotes,
            buddyHcpReverseReason,
            buddyHcpReverseNotes,
            postVisitNotesBuddy,
            abortedFollowUpAction,
            withdrawnReason,
            withdrawnNotes,
        } = personJobAdapter(personJobs);

        result.hcpId = hcpId;
        result.hcpName = hcpName;
        result.hcpType = hcpType;
        result.hcpGrade = hcpGrade;
        result.madeCurrentDateTime = madeCurrentDateTime;
        result.arrivedDateTime = arrivedDateTime;
        result.finishedDateTime = finishedDateTime;
        result.hcpAbortedDateTime = hcpAbortedDateTime;
        result.abortedBy = abortedBy;
        result.jobStatus = jobStatus ?? JobStatus.PENDING;
        result.jobStatusBeforeAbort = jobStatusBeforeAbort;
        result.controllerAbortedReason = controllerAbortedReason;
        result.controllerAbortedNotes = controllerAbortedNotes;
        result.controllerReverseReason = controllerReverseReason;
        result.controllerReverseNotes = controllerReverseNotes;
        result.hcpAbortedReason = hcpAbortedReason;
        result.hcpAbortedNotes = hcpAbortedNotes;
        result.hcpReverseReason = hcpReverseReason;
        result.hcpReverseNotes = hcpReverseNotes;
        result.postVisitNotes = postVisitNotes;
        result.buddyId = buddyId;
        result.buddyName = buddyName;
        result.buddyGrade = buddyGrade;
        result.buddyMadeCurrentDateTime = buddyMadeCurrentDateTime;
        result.buddyArrivedDateTime = buddyArrivedDateTime;
        result.buddyFinishedDateTime = buddyFinishedDateTime;
        result.buddyHcpAbortedDateTime = buddyHcpAbortedDateTime;
        result.buddyAbortedBy = buddyAbortedBy;
        result.buddyJobStatus = buddyJobStatus;
        result.buddyJobStatusBeforeAbort = buddyJobStatusBeforeAbort;
        result.buddyControllerAbortedReason = buddyControllerAbortedReason;
        result.buddyControllerAbortedNotes = buddyControllerAbortedNotes;
        result.buddyControllerReverseReason = buddyControllerReverseReason;
        result.buddyControllerReverseNotes = buddyControllerReverseNotes;
        result.buddyHcpAbortedReason = buddyHcpAbortedReason;
        result.buddyHcpAbortedNotes = buddyHcpAbortedNotes;
        result.buddyHcpReverseReason = buddyHcpReverseReason;
        result.buddyHcpReverseNotes = buddyHcpReverseNotes;
        result.postVisitNotesBuddy = postVisitNotesBuddy;
        result.abortedFollowUpAction = abortedFollowUpAction;
        result.withdrawnReason = withdrawnReason;
        result.withdrawnNotes = withdrawnNotes;
    }

    if (contactReferences?.length) {
        const contactRecords: v2.ContactRecord[] = [];

        contactReferences.forEach((contactReference) => {
            if (contactReference.contactRecord) {
                contactRecords.push(contactReference.contactRecord);
            }
        });

        const {
            addressLine1,
            addressLine2,
            addressLine3,
            town,
            county,
            postCode,
            geocodedAddress,
            contactNumber,
            additionalContactNumbers,
        } = contactRecordAdapter(contactRecords);

        result.addressLine1 = addressLine1;
        result.addressLine2 = addressLine2;
        result.addressLine3 = addressLine3;
        result.town = town;
        result.county = county;
        result.postCode = postCode;
        result.geocodedAddress = geocodedAddress;
        result.contactNumber = contactNumber;
        result.additionalContactNumbers = additionalContactNumbers;
    }

    if (admissions?.length) {
        const {
            referralPathway,
            referredBy,
            referrer,
            referralDateTime,
            systmOneRef,
            admission,
        } = admissionAdapter(admissions?.[0]);

        result.referralDateTime = referralDateTime;
        result.referralPathway = referralPathway;
        result.referredBy = referredBy;
        result.referrer = referrer;
        result.admission = admission;
        result.systmOneRef = systmOneRef;
    }

    if (patient) {
        const { nhsNumber, pds, odsCode, practice, staffPreferredGender } = patientAdapter(patient);

        result.nhsNumber = nhsNumber;
        result.pds = pds;
        result.odsCode = odsCode;
        result.practice = practice;
        result.staffPreferredGender = staffPreferredGender;
    }

    if (person) {
        const {
            firstName,
            lastName,
            middleName,
            dateOfBirth,
            languagesSpoken,
            gender,
        } = personAdapter(person);

        result.firstName = firstName;
        result.lastName = lastName;
        result.middleName = middleName;
        result.dateOfBirth = dateOfBirth;
        result.languagesSpoken = languagesSpoken;
        result.gender = gender;
    }

    if (notes) {
        const { notes: preVisitNotes, postVisitNotes, postVisitNotesBuddy } = noteAdapter(notes);

        result.notes = preVisitNotes;
        result.postVisitNotes = postVisitNotes;
        result.postVisitNotesBuddy = postVisitNotesBuddy;
    }

    if (input.vaccination) {
        const {
            hubId,
            vaccinationCategory,
            vaccinationType,
            consented,
            vaccineManufacturer,
            itineraryId,
            coAdministeredWith,
            vaccineBatchNumber,
            vaccineExpiryDate,
            vaccineSiteOfAdministration,
            flags,
            doseNumber,
            dateOfPreviousDose,
            manufacturerOfPreviousDose,
            dateOfDose1,
            dateOfDose2,
            dateOfDose3,
            dateOfDose3Primary,
            dateOfDose4,
            manufacturerOfDose1,
            manufacturerOfDose2,
            manufacturerOfDose3,
            manufacturerOfDose3Primary,
            manufacturerOfDose4,
            odsCode,
            practice,
        } = vaccinationAdapter(input);

        result.hubId = hubId;
        result.vaccinationCategory = vaccinationCategory;
        result.vaccinationType = vaccinationType;
        result.consented = consented;
        result.vaccineManufacturer = vaccineManufacturer;
        result.itineraryId = itineraryId;
        result.coAdministeredWith = coAdministeredWith;
        result.vaccineBatchNumber = vaccineBatchNumber;
        result.vaccineExpiryDate = vaccineExpiryDate;
        result.vaccineSiteOfAdministration = vaccineSiteOfAdministration;
        result.flags = flags;
        result.doseNumber = doseNumber;
        result.dateOfPreviousDose = dateOfPreviousDose;
        result.manufacturerOfPreviousDose = manufacturerOfPreviousDose;
        result.dateOfDose1 = dateOfDose1;
        result.dateOfDose2 = dateOfDose2;
        result.dateOfDose3 = dateOfDose3;
        result.dateOfDose3Primary = dateOfDose3Primary;
        result.dateOfDose4 = dateOfDose4;
        result.manufacturerOfDose1 = manufacturerOfDose1;
        result.manufacturerOfDose2 = manufacturerOfDose2;
        result.manufacturerOfDose3 = manufacturerOfDose3;
        result.manufacturerOfDose3Primary = manufacturerOfDose3Primary;
        result.manufacturerOfDose4 = manufacturerOfDose4;
        result.odsCode = odsCode;
        result.practice = practice;

        // Vaccination jobs expect availableFrom/availableTo fields to be in the format 'HH:mm'
        if (availableFrom) {
            result.availableFrom = toLuxon(availableFrom).toFormat('HH:mm');
        }
        if (availableTo) {
            result.availableTo = toLuxon(availableTo).toFormat('HH:mm');
        }
    }

    if (input.route) {
        const { itineraryId, routeType } = input.route;

        result.itineraryId = itineraryId;
        result.routeType = routeType;
    }

    if (input.onDemand) {
        const { onDemandType, specialities, priceInPence, hidePrice, blockSize } = input.onDemand;

        result.jobType = onDemandType;
        result.speciality = specialities ?? undefined;
        result.hcpPriceInPence = priceInPence ?? undefined;
        result.priceInPence = priceInPence ?? undefined;
        result.hidePriceInApp = hidePrice ?? undefined;
        result.blockSize = blockSize ?? undefined;

        if (result.dateOfBirth) {
            const ageInYears = DateTime.utc().diff(DateTime.fromISO(result.dateOfBirth), 'years')
                .years;
            result.age = Math.floor(ageInYears);
        }
    }

    let resultNoUndefined: ReturnType = {};

    Object.entries(result).forEach(([key, value]) => {
        if (value !== undefined) {
            resultNoUndefined = {
                ...resultNoUndefined,
                [key as keyof ReturnType]: value,
            };
        }
    });

    return resultNoUndefined;
};
